<template>
  <v-card flat class="fill-height grey lighten-4" min-height="100vh">
    <v-overlay v-if="!authUser && !initializing && !closedOverlay" :value="true" opacity="0.9" z-index="100">
      <v-card width="400" class="pa-6 light-blue darken-4 white--text rounded-xl">
        <v-icon @click="closedOverlay = true" style="position: absolute; top: 15px; right: 15px;">mdi-close</v-icon>
        <v-card-title class="headline font-weight-bold">Generation Analytics</v-card-title>
        <v-card-text>
          <v-text-field v-model="loginEmail" label="Email" dark filled rounded prepend-inner-icon="mdi-email" />
          <v-text-field v-model="loginPassword" label="Passwort" type="password" dark filled rounded prepend-inner-icon="mdi-lock" @keyup.enter="handleLogin" />
          <v-alert v-if="authError" type="error" dense class="mt-2" rounded="lg">{{ authError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="white" light large rounded class="px-8 font-weight-bold" :loading="authLoading" @click="handleLogin">Login</v-btn>
        </v-card-actions>
      </v-card>
    </v-overlay>

    <v-container v-if="authUser" fluid>
      <v-toolbar flat color="white" class="mb-4 rounded-lg elevation-1">
        <v-toolbar-title class="font-weight-bold">
          <v-icon left color="blue">mdi-chart-line</v-icon> AI Generation Monitor
        </v-toolbar-title>

        <v-divider vertical class="mx-4" inset />

        <v-btn-toggle v-model="statusFilter" mandatory dense color="primary" class="rounded-lg mr-4">
          <v-btn value="all" small>Alle</v-btn>
          <v-btn value="success" small color="green lighten-5" class="green--text">Erfolge</v-btn>
          <v-btn value="error" small color="red lighten-5" class="red--text">Fehler</v-btn>
        </v-btn-toggle>

        <v-spacer />

        <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined v-bind="attrs" v-on="on" class="mr-2 rounded-lg">
              <v-icon left>mdi-calendar</v-icon> {{ formattedDateDisplay }}
            </v-btn>
          </template>
          <v-date-picker v-model="selectedDate" @input="dateMenu = false" no-title scrollable />
        </v-menu>

        <v-select
            v-model="compareRange"
            :items="compareOptions"
            outlined hide-details dense
            style="max-width: 140px;"
            class="mr-4 rounded-lg"
        />

        <v-btn color="grey lighten-3" depressed fab x-small @click="handleLogout">
          <v-icon small>mdi-logout</v-icon>
        </v-btn>
      </v-toolbar>

      <v-row dense class="mb-4">
        <v-col v-for="(stat, i) in stats" :key="i" cols="12" sm="6" md="3">
          <v-card class="pa-4 rounded-lg elevation-1 fill-height border-left-highlight" :style="{ borderLeftColor: stat.trendColor || '#eee' }">
            <div class="text-overline mb-1 grey--text font-weight-bold">{{ stat.label }}</div>
            <div class="d-flex align-center">
              <div class="text-h4 font-weight-black mr-4">{{ stat.value }}</div>
              <v-chip v-if="stat.trend !== null" :color="stat.trend > 0 ? 'red lighten-4' : 'green lighten-4'" :text-color="stat.trend > 0 ? 'red darken-4' : 'green darken-4'" small label class="font-weight-bold">
                <v-icon x-small left>{{ stat.trend > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                {{ Math.abs(stat.trend) }}
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="rounded-lg elevation-1">
        <v-data-table
            :headers="headers"
            :items="filteredLogs"
            :loading="loading"
            :search="search"
            sort-by="timestamp"
            sort-desc
        >
          <template v-slot:top>
            <v-text-field v-model="search" label="Prompt, Style oder User-ID suchen..." class="pa-4" prepend-inner-icon="mdi-magnify" clearable hide-details filled rounded dense />
          </template>

          <template v-slot:item.status="{ item }">
            <v-icon :color="item.status === 'success' ? 'green' : 'red'">
              {{ item.status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
          </template>

          <template v-slot:item.images="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar size="50" rounded class="grey lighten-3 border cursor-pointer shadow-sm mr-2" @click="openImage(item.originalImageUrl)">
                <v-img :src="item.originalImageUrl" cover />
              </v-avatar>

              <v-icon v-if="item.status === 'success'" small color="grey">mdi-arrow-right</v-icon>

              <v-avatar v-if="item.resultImageUrl" size="50" rounded class="grey lighten-3 border cursor-pointer shadow-sm ml-2" @click="openImage(item.resultImageUrl)">
                <v-img :src="item.resultImageUrl" cover />
              </v-avatar>
            </div>
          </template>

          <template v-slot:item.errorCode="{ item }">
            <v-chip v-if="item.errorCode" :color="getErrorColor(item.errorCode)" dark small label class="font-weight-bold">
              {{ item.errorCode }}
            </v-chip>
            <span v-else class="grey--text">—</span>
          </template>

          <template v-slot:item.prompt="{ item }">
            <div class="text-truncate font-weight-medium" style="max-width: 350px;">{{ item.prompt }}</div>
            <div class="text-caption blue--text font-weight-bold">STYLE: {{ item.styleId }}</div>
          </template>

          <template v-slot:item.timestamp="{ item }">
            {{ formatTime(item.timestamp) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="blue" @click="selectedLog = item">
              <v-icon>mdi-information-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog :value="!!selectedLog" @input="selectedLog = null" max-width="1100">
        <v-card v-if="selectedLog" class="rounded-xl overflow-hidden">
          <v-toolbar flat dark :color="selectedLog.status === 'success' ? 'green darken-3' : 'red darken-4'">
            <v-toolbar-title class="font-weight-bold">
              {{ selectedLog.status === 'success' ? 'Generierung Erfolgreich' : 'Fehler-Analyse' }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn icon @click="selectedLog = null"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>

          <v-card-text class="pa-8">
            <v-row>
              <v-col cols="12" md="7">
                <v-row dense>
                  <v-col cols="6">
                    <div class="text-caption font-weight-bold mb-2 grey--text">INPUT (ORIGINAL)</div>
                    <v-img :src="selectedLog.originalImageUrl" class="rounded-xl grey lighten-3 border elevation-2" contain max-height="450" />
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption font-weight-bold mb-2 grey--text">RESULTAT (KI)</div>
                    <v-img v-if="selectedLog.resultImageUrl" :src="selectedLog.resultImageUrl" class="rounded-xl grey lighten-3 border elevation-2" contain max-height="450" />
                    <div v-else class="rounded-xl grey lighten-4 border d-flex align-center justify-center fill-height" style="min-height: 300px;">
                      <v-icon x-large color="grey lighten-1">mdi-image-off</v-icon>
                    </div>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="5">
                <div v-if="selectedLog.status === 'error'">
                  <div class="text-h6 mb-2 red--text font-weight-bold">Fehlermeldung</div>
                  <v-alert colored-border border="left" color="red" elevation="1" class="text-body-2">
                    <strong>{{ selectedLog.errorCode }}:</strong> {{ selectedLog.error }}
                  </v-alert>
                </div>

                <div class="text-h6 mt-4 mb-2 font-weight-bold grey--text text--darken-2">Finaler Prompt</div>
                <div class="pa-4 grey lighten-4 rounded-lg border text-body-2 font-italic mb-4">
                  "{{ selectedLog.prompt }}"
                </div>

                <v-row dense class="mb-4">
                  <v-col cols="6"><div class="text-caption grey--text">User ID</div><div class="text-body-2 font-weight-bold truncate">{{ selectedLog.userId }}</div></v-col>
                  <v-col cols="6"><div class="text-caption grey--text">Finish Reason</div><v-chip x-small label>{{ selectedLog.finishReason || 'N/A' }}</v-chip></v-col>
                </v-row>

                <div v-if="selectedLog.safetyRatings" class="mt-4">
                  <div class="text-h6 mb-3 grey--text text--darken-2 font-weight-bold">KI-Sicherheitsbewertung</div>
                  <v-row dense>
                    <v-col v-for="rate in selectedLog.safetyRatings" :key="rate.category" cols="12" sm="6">
                      <v-card flat class="pa-2 border rounded-lg d-flex justify-space-between align-center">
                        <span class="text-caption font-weight-bold">{{ formatCategory(rate.category) }}</span>
                        <v-chip x-small :color="getRatingColor(rate.probability)" dark class="font-weight-bold">{{ rate.probability }}</v-chip>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="imageDialog" max-width="95vw">
        <v-img :src="activeImg" width="100%" @click="imageDialog = false" contain class="rounded-lg" />
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import { initializeApp, getApp, getApps } from "firebase/app";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";

export default {
  name: "GlobalGenerationMonitor",

  data: () => ({
    initializing: true,
    authLoading: false,
    authUser: null,
    authError: null,
    loginEmail: '',
    loginPassword: '',
    closedOverlay: false,

    loading: false,
    dateMenu: false,
    statusFilter: 'all',
    search: '',
    imageDialog: false,
    activeImg: '',
    compareRange: 'day',
    compareOptions: [{ text: 'vs Gestern', value: 'day' }, { text: 'vs Vorwoche', value: 'week' }],

    selectedDate: new Date().toISOString().substr(0, 10),
    logs: [],
    prevLogs: [],
    selectedLog: null,

    headers: [
      { text: 'St.', value: 'status', width: '50px' },
      { text: 'Vergleich (In -> Out)', value: 'images', sortable: false },
      { text: 'Zeit', value: 'timestamp' },
      { text: 'Fehlercode', value: 'errorCode' },
      { text: 'Prompt & Style', value: 'prompt' },
      { text: 'Details', value: 'actions', sortable: false, align: 'end' },
    ]
  }),

  computed: {
    formattedDateDisplay() {
      return new Date(this.selectedDate).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    },
    filteredLogs() {
      if (this.statusFilter === 'all') return this.logs;
      return this.logs.filter(l => l.status === this.statusFilter);
    },
    stats() {
      const currentTotal = this.logs.length;
      const currentErrors = this.logs.filter(l => l.status === 'error').length;
      const currentSuccess = currentTotal - currentErrors;
      const errorRate = currentTotal > 0 ? ((currentErrors / currentTotal) * 100).toFixed(1) : 0;

      const prevTotal = this.prevLogs.length;
      const prevErrors = this.prevLogs.filter(l => l.status === 'error').length;

      return [
        { label: 'Generierungen', value: currentTotal, trend: currentTotal - prevTotal, trendColor: '#2196F3' },
        { label: 'Fehlerquote', value: errorRate + '%', trend: null, trendColor: errorRate > 20 ? '#F44336' : '#4CAF50' },
        { label: 'Erfolge', value: currentSuccess, trend: currentSuccess - (prevTotal - prevErrors), trendColor: '#4CAF50' },
        { label: 'Safety Blocks', value: this.logs.filter(l => l.errorCode === 'failed-precondition').length, trend: null, trendColor: '#FF9800' },
      ];
    }
  },

  watch: {
    selectedDate() { this.loadData(); },
    compareRange() { this.loadData(); }
  },

  mounted() { this.initAuthObserver(); },

  methods: {
    getIsolatedFirebase() {
      const firebaseConfig = {
        apiKey: "AIzaSyAV8jI0EekE4OMuClaQiF4WpuJeqnZ2AMA",
        authDomain: "ohmyart-ai.firebaseapp.com",
        projectId: "ohmyart-ai",
        storageBucket: "ohmyart-ai.firebasestorage.app",
        messagingSenderId: "3734662653",
        appId: "1:3734662653:web:f087ef05b947f3c218fd94"
      };
      const name = "gen_monitor_app";
      const app = getApps().find(a => a.name === name) ? getApp(name) : initializeApp(firebaseConfig, name);
      return { auth: getAuth(app), db: getFirestore(app) };
    },

    initAuthObserver() {
      const { auth } = this.getIsolatedFirebase();
      onAuthStateChanged(auth, (user) => {
        this.authUser = user;
        this.initializing = false;
        if (user) this.loadData();
      });
    },

    async handleLogin() {
      if (!this.loginEmail || !this.loginPassword) return;
      this.authLoading = true;
      try {
        const { auth } = this.getIsolatedFirebase();
        await signInWithEmailAndPassword(auth, this.loginEmail, this.loginPassword);
      } catch (e) { this.authError = "Login fehlgeschlagen."; }
      finally { this.authLoading = false; }
    },

    async handleLogout() {
      const { auth } = this.getIsolatedFirebase();
      await signOut(auth);
    },

    async loadData() {
      if (!this.authUser) return;
      this.loading = true;
      const { db } = this.getIsolatedFirebase();
      const dateKey = this.selectedDate.replace(/-/g, '_');

      const d = new Date(this.selectedDate);
      if (this.compareRange === 'day') d.setDate(d.getDate() - 1);
      else d.setDate(d.getDate() - 7);
      const prevDateKey = d.toISOString().substr(0, 10).replace(/-/g, '_');

      try {
        const currentSnap = await getDocs(collection(db, 'generations', dateKey, 'logs'));
        this.logs = currentSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const prevSnap = await getDocs(collection(db, 'generations', prevDateKey, 'logs'));
        this.prevLogs = prevSnap.docs.map(doc => doc.data());
      } catch (e) {
        console.error("Data fetch error", e);
      } finally { this.loading = false; }
    },

    getErrorColor(code) {
      if (code === 'failed-precondition') return 'orange darken-1';
      if (code === 'internal') return 'red darken-3';
      return 'grey darken-1';
    },

    getRatingColor(prob) {
      const map = { 'HIGH': 'red', 'MEDIUM': 'orange', 'LOW': 'yellow darken-3', 'NEGLIGIBLE': 'green' };
      return map[prob] || 'grey';
    },

    formatCategory(cat) { return cat.replace('HARM_CATEGORY_', '').replace('_', ' '); },

    formatTime(ts) {
      if (!ts) return '';
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    },

    openImage(url) {
      if (!url) return;
      this.activeImg = url;
      this.imageDialog = true;
    }
  }
};
</script>

<style scoped>
.border { border: 1px solid rgba(0,0,0,0.12) !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.border-left-highlight { border-left: 6px solid #eee; }
.cursor-pointer { cursor: pointer; }
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>